import React, { useRef, useState } from 'react'

import { PaginatedMenuItemsQuery } from 'src/apollo/onlineOrdering'
import { MenuConfig } from 'src/apollo/sites'
import useOnVisible from 'src/lib/js/hooks/useOnVisible'
import MenuSection from 'src/public/components/default_template/menu_section/MenuSection'
import { LoadingSpinner } from 'src/shared/components/common/loading_spinner/LoadingSpinner'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'

type Props = {
  menuItems?: NonNullable<
    PaginatedMenuItemsQuery['paginatedMenuItems']['items']
  >
  products?: NonNullable<
    PaginatedMenuItemsQuery['paginatedMenuItems']['products']
  >
  loading: boolean
  loadingNewFilterData: boolean
  fetchMore: (offset: number) => void
  menuConfig: MenuConfig | null | undefined
  hideOutOfStockItems?: boolean | null
  channelGuid?: string
  hasMore?: boolean
  variableMenuSpacing?: boolean
}

export const PaginatedMenu = ({
  menuItems,
  products,
  loading,
  loadingNewFilterData,
  fetchMore,
  menuConfig,
  hideOutOfStockItems,
  channelGuid,
  hasMore = true,
  variableMenuSpacing
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const { restaurant: siteRestaurant, ooRestaurant: restaurant } =
    useRestaurant()
  const restaurantGuid = restaurant?.guid
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false)

  // Calculate total menu items for pagination
  const totalItems = (menuItems?.length || 0) + (products?.length || 0)

  useOnVisible(
    ref,
    () => {
      if (loading || !hasMore) {
        return
      }

      setShowLoadingSpinner(true)
      fetchMore(totalItems)

      // artificially delay removing the loading spinner to force the UI for fast menu loads
      setTimeout(() => {
        setShowLoadingSpinner(false)
      }, 2000)
    },
    { rootMargin: '1px 1px 1px 1px' }
  )

  return (
    <div className='paginatedMenu'>
      <div className='paddedContentWrapper'>
        <div className='paddedContent'>
          <MenuSection
            channelGuid={channelGuid}
            restaurantGuid={restaurantGuid}
            orientation={siteRestaurant.config.ooConfig?.cardOrientation}
            menuConfig={{
              format: menuConfig?.format,
              colors: menuConfig?.colors
            }}
            menuItems={
              hideOutOfStockItems
                ? menuItems?.filter((item) => !item.outOfStock)
                : menuItems
            }
            products={
              hideOutOfStockItems
                ? products?.filter((product) => !product.outOfStock)
                : products
            }
            loading={loading || loadingNewFilterData}
            variableMenuSpacing={variableMenuSpacing}
          />
        </div>
      </div>
      <div ref={ref} className='loader'>
        {showLoadingSpinner && !!ref.current && totalItems > 0 && (
          <LoadingSpinner />
        )}
      </div>
    </div>
  )
}
